import { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { createTheme, useMediaQuery } from "@mui/material";

// MUI Components
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import CommentCard from "./CommentCard";

// Constants
import { conditionTitles } from "../../../enums";

// Utils
import { sortByDate } from "../../../utils/dateHelper";

// Contexts
import { ReportEachSectionContext } from "../../../contexts/CommentContext";
import { ValidatePDFContext } from "../../../contexts/ValidatePDFContext";

const CommentsList = ({ isVisualField, isEsterman = false }) => {
  const { t } = useTranslation();
  const theme = createTheme();
  const isWiderThanSmallScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const { forBackendPdf } = useContext(ValidatePDFContext);
  const { comments } = useContext(ReportEachSectionContext);

  const maxHeight = forBackendPdf ? "100%" : 120;

  // display newest comments first
  const activeComments = useMemo(
    () => sortByDate(comments, "creationDate"),
    [comments]
  );

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <Grid container>
        {(isVisualField || isEsterman) && <Grid item xs={1} />}
        <Grid
          id="add-comments-header"
          container
          item
          xs={isVisualField || isEsterman ? 11 : 12}
          sm={isVisualField || isEsterman ? 11 : 12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          padding="10px"
          backgroundColor="black"
          borderRadius="4px 4px 0px 0px"
        >
          <Typography color="#FFF" fontWeight={600}>
            {t("report_comments")}
          </Typography>
        </Grid>
      </Grid>
      <Grid container>
        {(isVisualField || isEsterman) && <Grid item xs={1} />}
        {activeComments?.length > 0 ? (
          <>
            <Grid
              id="comments-list"
              container
              item
              xs={isVisualField ? 8 : isEsterman ? 11 : 12}
              sm={isVisualField ? 8.5 : isEsterman ? 11 : 12}
              border={1}
              borderRadius="0px 0px 0px 4px"
              maxHeight={maxHeight}
              overflow="auto"
            >
              {activeComments.map((comment, index) => (
                <CommentCard
                  key={index}
                  creationDate={comment.creationDate}
                  comment={comment.content}
                  conditions={comment.conditions}
                  prevConditions={
                    index !== activeComments.length - 1
                      ? activeComments[index + 1].conditions
                      : []
                  }
                />
              ))}
            </Grid>
            {isVisualField && (
              <Grid
                item
                xs={3}
                sm={2.5}
                border={1}
                borderRadius="0px 0px 4px 0px"
                borderLeft={0}
                height={maxHeight}
                px="10px"
              >
                {Object.keys(conditionTitles).length && (
                  <List>
                    {Object.keys(conditionTitles).map((key) => (
                      <ListItem key={key} disablePadding>
                        <Checkbox
                          checked={activeComments[0].conditions.includes(key)}
                          disabled
                          style={{
                            padding: 0,
                            color: "#5B6D7A",
                            "&.Mui-checked": { color: "#5B6D7A" },
                            "&.Mui-disabled": { color: "#5B6D7A" },
                          }}
                        />
                        <ListItemText
                          primary={conditionTitles[key]}
                          primaryTypographyProps={{
                            paddingLeft: isWiderThanSmallScreen ? 1 : 0,
                            fontSize: isWiderThanSmallScreen ? 16 : 14,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Grid>
            )}
          </>
        ) : (
          <Grid
            item
            xs={isVisualField || isEsterman ? 11 : 12}
            pt={2}
            pb={2}
            border={1}
          >
            <Typography textAlign="center">{t("no_comments")}</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CommentsList;
